import { useState } from "react";
import { useVeiculos } from "@/features/veiculos/hooks/useVeiculos";
import { veiculoService } from "@/features/veiculos/services/veiculoService";
import type { VeiculoListagemDto, SalvarVeiculoRequest } from "@/features/veiculos/types";
import { TIPOS_CARROCERIA } from "@/features/transporte/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Truck } from "lucide-react";
import { toast } from "sonner";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const UFS_BR = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT",
  "PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO",
];

const FORM_VAZIO: SalvarVeiculoRequest = {
  placa: "", placaCidade: "", placaUf: "",
  renavam: "", chassi: "", chassiRemarcado: false,
  marca: "", modelo: "", ano: new Date().getFullYear(),
  anoModelo: new Date().getFullYear(), tipoCombustivel: 1, avaliacao: 0,
  temRastreador: false, rastreador: "", controlaKm: false, kmInicial: 0,
  frotaId: 0, faixaConsumoId: 0, observacao: "", isInativo: false,
  dtTrocaFluidoRadiador: null, dtLimpezaRadiador: null,
  codigoMarcaModelo: "", marcaVeiculoId: 0, modeloVeiculoId: 0,
  modeloCarroceriaId: 0, corId: 0, lotacao: "", eixos: 2, distanciaEixo: 0,
  tipoCarga: "", potenciaCv: 0, cilindradas: 0, tipoFreio: 1,
  condicaoVeiculo: 1, restricao: "", numeroMotor: "", motorista: "",
  pesoBruto: 0, pesoLiquido: 0, rntrc: "",
  seguradora: "", exercicio: 0, valorCasco: 0,
  danosMaterialValor: 0, danosCorporaisValor: 0,
};

export default function VeiculoTransportePage() {
  const [busca, setBusca] = useState("");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const { query, criar, atualizar, excluir } = useVeiculos(busca || undefined, 1, 200);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editando, setEditando] = useState<VeiculoListagemDto | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [form, setForm] = useState<SalvarVeiculoRequest>(FORM_VAZIO);

  const veiculos = query.data?.itens ?? [];
  const filtrados = mostrarInativos
    ? veiculos
    : veiculos.filter((v) => !v.isInativo);

  const paginacao = usePaginacaoLocal(filtrados);

  const abrirDialog = async (v?: VeiculoListagemDto) => {
    if (v) {
      setEditando(v);
      setDialogOpen(true);
      setFormLoading(true);
      try {
        const full = await veiculoService.obterPorId(v.id);
        setForm({
          placa: full.placa ?? "",
          placaCidade: full.placaCidade ?? "",
          placaUf: full.placaUf ?? "",
          renavam: full.renavam ?? "",
          chassi: full.chassi ?? "",
          chassiRemarcado: full.chassiRemarcado,
          marca: full.marca ?? "",
          modelo: full.modelo ?? "",
          ano: full.ano,
          anoModelo: full.anoModelo,
          tipoCombustivel: full.tipoCombustivel,
          avaliacao: full.avaliacao,
          temRastreador: full.temRastreador,
          rastreador: full.rastreador ?? "",
          controlaKm: full.controlaKm,
          kmInicial: full.kmInicial,
          frotaId: full.frotaId,
          faixaConsumoId: full.faixaConsumoId,
          observacao: full.observacao ?? "",
          isInativo: full.isInativo,
          dtTrocaFluidoRadiador: full.dtTrocaFluidoRadiador ?? null,
          dtLimpezaRadiador: full.dtLimpezaRadiador ?? null,
          codigoMarcaModelo: full.codigoMarcaModelo ?? "",
          marcaVeiculoId: full.marcaVeiculoId,
          modeloVeiculoId: full.modeloVeiculoId,
          modeloCarroceriaId: full.modeloCarroceriaId,
          corId: full.corId,
          lotacao: full.lotacao ?? "",
          eixos: full.eixos,
          distanciaEixo: full.distanciaEixo,
          tipoCarga: full.tipoCarga ?? "",
          potenciaCv: full.potenciaCv,
          cilindradas: full.cilindradas,
          tipoFreio: full.tipoFreio,
          condicaoVeiculo: full.condicaoVeiculo,
          restricao: full.restricao ?? "",
          numeroMotor: full.numeroMotor ?? "",
          motorista: full.motorista ?? "",
          pesoBruto: full.pesoBruto,
          pesoLiquido: full.pesoLiquido,
          rntrc: full.rntrc ?? "",
          seguradora: full.seguradora ?? "",
          exercicio: full.exercicio,
          valorCasco: full.valorCasco,
          danosMaterialValor: full.danosMaterialValor,
          danosCorporaisValor: full.danosCorporaisValor,
        });
      } finally {
        setFormLoading(false);
      }
    } else {
      setEditando(null);
      setForm(FORM_VAZIO);
      setDialogOpen(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editando) {
        await atualizar.mutateAsync({ id: editando.id, req: form });
        toast.success("Veículo atualizado com sucesso!");
      } else {
        await criar.mutateAsync(form);
        toast.success("Veículo cadastrado com sucesso!");
      }
      setDialogOpen(false);
    } catch {
      // error handled globally via mutationCache
    }
  };

  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Veículos</h1>
          <p className="text-muted-foreground">Cadastro de veículos para CT-e e MDFe</p>
        </div>
        <Button onClick={() => abrirDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Veículo
        </Button>
      </div>

      <div className="flex gap-4 items-center">
        <div className="max-w-sm flex-1">
          <Input
            placeholder="Buscar por placa, marca ou modelo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={mostrarInativos}
            onChange={(e) => setMostrarInativos(e.target.checked)}
          />
          Exibir inativos
        </label>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Placa</TableHead>
              <TableHead>UF</TableHead>
              <TableHead>RENAVAM</TableHead>
              <TableHead>RNTRC</TableHead>
              <TableHead>Tara (kg)</TableHead>
              <TableHead>Capacidade (kg)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((v) => (
              <TableRow key={v.id}>
                <TableCell className="font-medium font-mono">{v.placa ?? "-"}</TableCell>
                <TableCell>{v.placaUf ?? "-"}</TableCell>
                <TableCell>{v.renavam ?? "-"}</TableCell>
                <TableCell>{v.rntrc ?? "-"}</TableCell>
                <TableCell>{v.pesoBruto ? v.pesoBruto.toLocaleString("pt-BR") : "-"}</TableCell>
                <TableCell>{v.pesoLiquido ? v.pesoLiquido.toLocaleString("pt-BR") : "-"}</TableCell>
                <TableCell>
                  <Badge variant={v.isInativo ? "secondary" : "default"}>
                    {v.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => abrirDialog(v)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!v.isInativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(v.id)}>
                        <Truck className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum veículo encontrado para esta busca." : "Nenhum veículo cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* ── Dialog Criar / Editar ─────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editando ? "Editar Veículo" : "Novo Veículo"}</DialogTitle>
          </DialogHeader>
          {formLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando dados...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="placa">Placa *</Label>
                  <Input
                    id="placa"
                    value={form.placa}
                    onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })}
                    required
                    placeholder="ABC1D23"
                    maxLength={10}
                    className="font-mono uppercase"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="placaUf">UF *</Label>
                  <Select
                    value={form.placaUf}
                    onValueChange={(v) => setForm({ ...form, placaUf: v })}
                  >
                    <SelectTrigger id="placaUf">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {UFS_BR.map((uf) => (
                        <SelectItem key={uf} value={uf}>
                          {uf}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="renavam">RENAVAM</Label>
                  <Input
                    id="renavam"
                    value={form.renavam ?? ""}
                    onChange={(e) => setForm({ ...form, renavam: e.target.value })}
                    maxLength={20}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rntrc">RNTRC</Label>
                  <Input
                    id="rntrc"
                    value={form.rntrc ?? ""}
                    onChange={(e) => setForm({ ...form, rntrc: e.target.value })}
                    maxLength={20}
                    placeholder="00000000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tipoCarroceria">Tipo de Carroceria</Label>
                  <Select
                    value={form.tipoCarga ?? ""}
                    onValueChange={(v) => setForm({ ...form, tipoCarga: v })}
                  >
                    <SelectTrigger id="tipoCarroceria">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_CARROCERIA.map((tipo) => (
                        <SelectItem key={tipo} value={tipo} className="capitalize">
                          {tipo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pesoBruto">Tara (kg)</Label>
                  <Input
                    id="pesoBruto"
                    type="number"
                    value={form.pesoBruto || ""}
                    onChange={(e) =>
                      setForm({ ...form, pesoBruto: e.target.value ? Number(e.target.value) : 0 })
                    }
                    min={0}
                    step={0.01}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pesoLiquido">Capacidade (kg)</Label>
                  <Input
                    id="pesoLiquido"
                    type="number"
                    value={form.pesoLiquido || ""}
                    onChange={(e) =>
                      setForm({ ...form, pesoLiquido: e.target.value ? Number(e.target.value) : 0 })
                    }
                    min={0}
                    step={0.01}
                  />
                </div>

                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="ativoVeic"
                    checked={!form.isInativo}
                    onChange={(e) => setForm({ ...form, isInativo: !e.target.checked })}
                  />
                  <Label htmlFor="ativoVeic">Ativo</Label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={criar.isPending || atualizar.isPending}>
                  Salvar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ── AlertDialog Inativar ─────────────────────────────────────── */}
      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar veículo</AlertDialogTitle>
            <AlertDialogDescription>
              O veículo será inativado e não aparecerá nos selects de novos CT-es e MDFes. Histórico de
              documentos já emitidos é preservado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (inativarId) {
                  excluir.mutate(inativarId);
                  setInativarId(null);
                  toast.success("Veículo inativado com sucesso!");
                }
              }}
            >
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
